
import { LightningElement, api } from 'lwc';
import { mapRowsToColumns, handleTableScrollBehavior } from '../../../utils/tableUtils.js';
import {iconByDataType, iconPath, dateObjToYearFirst} from '../../../utils/basicUtils.js';


export default class sldsTable extends LightningElement {

    // Show spinner while loading
    isLoading = false;

    // ------- Pagination -------- \\
    paginatedRows = {};
    pageInfo = {pageCount: 1, activePage: null};
    isNewPage = false;
    // --------------------------- \\

    // ------- Modal Config ------- \\
    modalConfig = {
        tableModalMessage: null,
        modalCancelLabel: null,
        modalOkayLabel: null,
        tableModalHandler: null
    };
    popoverEditCopy = {};
    checkboxArray = [];
    // --------------------------- \\

    // ------ Menu Dropdown ------ \\
    rowActionOptions = [
        {label: 'Edit Row', value: 'edit'},
        {label: 'Delete Row', value: 'delete'},
        {label: 'Close Trade', value: 'closeTrade'}
    ];
    relativeContainer;
    // --------------------------- \\

    // Track when user clicks escape to not save their edit
    isEscape = false;
    isPreventBlur = false;

    // ------- Resize Cols ------- \\
    _tableThColumn = null;
    _pageX = null;
    _tableThWidth = null;
    _initWidths = null;
    // --------------------------- \\

    // ---------- Toast ---------- \\
    showToastError = false;
    toastTitle;
    toastError;
    // --------------------------- \\

    isDataLoaded=false;
    isTableEdited = false;

    // --------- Drafts ---------- \\
    draftOriginalValues = [];  // Store the original value of each field that was edited
    draftValues = [];         // Store all current values of each edited field 
    popoverEdit = {};
    picklistOptions = [];   // if is a picklist type with editing popover ---> store the options
    // --------------------------- \\

    // ------- Table Data -------- \\
    localRows = [];
    localColumns = [];
    _rows = [];  // Private variable for the records array data
    _columns = [];  // Private variable for the columns
    // --------------------------- \\

    @api suppressRowActions = false;

    @api 
    set columnsBg(val) {
        this._columnsBg = val;
        if (val) {
            this._applyVarsDirect('--gw-table-columns-color-background', val);
        } else {
            this._applyVarsDirect('--gw-table-columns-color-background', 'var(--slds-g-color-neutral-base-95, #f3f3f3)');
        }
    }
    get columnsBg() {
        return this._columnsBg;
    }

    // ======================== Public Properties ========================== \\
    @api // Display paginator Footer at bottom and set max rows to 50
    set isPaginator(val) {
        this._isPaginator = val && (val==="true" || val===true);
    }
    get isPaginator() {
        return this._isPaginator;
    }
    @api // Actions for the row-action buttons
    set rowActions(value) {
        this._rowActions = value;
    }
    get rowActions() {
        return this._rowActions;
    }
    @api // Columns for the table
    set columns(value) {
        this._initWidths = null;
        this._columns = value;
        this.parseRowsAndColumns();
    }
    get columns() {
        return this._columns;
    }
    @api // Rows for the table
    set rows(value) {
        this.paginatedRows  = {};
        this.pageInfo = {pageCount: 1, activePage: 1};
        this._rows = value;
        this.parseRowsAndColumns();
    }
    get rows() {
        return this._rows;
    }
    @api // Max height where scroll is triggered
    set maxHeight(value) {
        this._maxHeight = value ? value : '70vh';
    }
    get maxHeight() {
        return this._maxHeight || 'calc(70vh - 50px)'; // Default if not set
    }
    isMaxHeightRendered = false;
    origMaxHeight;
    _sortedCol;
    @api // Set the initial sortedByColumn when a list view loads
    set sortedCol(val) { this._sortedCol = val; }
    get sortedCol() { return this._sortedCol; }
    // ===================================================================== \\

    get footerBgClass() {
        return this.isPaginator
            ? 'slds-popover__footer slds-p-around_medium footer-light-medium'
            : 'slds-popover__footer slds-p-around_medium';
    }
    
    // ========================= Public Methods ============================ \\
    // Reset the column widths back to their original config
    @api resetColumnWidths() {
        const tableElement = this.template.querySelector('table');
        if (!tableElement) return;

        const container = this.template.querySelector('.custom-table-container');
        if (!container) return;
    
        // Reset table width to its natural size
        tableElement.style.width = '';
    
        // Ensure stored original column widths exist
        if (!this._initWidths || this._initWidths.length === 0) return;
    
        const allThs = tableElement.querySelectorAll('thead th');
        if (!allThs) return;
        const containerWidth = container.offsetWidth; // Viewport width of the table
    
        // Restore original column widths so they fit within the viewport
        allThs.forEach((th, index) => {
            if (this._initWidths[index] !== undefined) {
                th.style.width = `${(this._initWidths[index] / 100) * containerWidth}px`;
            }
        });
    
        // Ensure the table fits within the viewport and removes any horizontal scroll
        container.style.overflowX = 'hidden'; // Temporarily remove scroll
        container.style.overflowY = 'hidden'; // Temporarily remove scroll
        setTimeout(() => {
            container.style.overflowX = 'auto'; // Restore natural scrolling behavior
            container.style.overflowY = 'auto';
        }, 10);
    }
    // Return the current column widths as %.  Structure: [ {fieldName: "<col.fieldName>", width: "x%"} ]
    @api getColumnWidths() {
        // Get the table element and ensure it exists
        const tableElement = this.template.querySelector('table');
        if (!tableElement) {
            return [];
        }
    
        // Retrieve all header cells
        const allThs = tableElement.querySelectorAll('thead th');
        const totalTableWidth = tableElement.offsetWidth;
    
        // Build an array of column configurations
        const currentWidths = [];
        allThs.forEach((th, index) => {
            // Use the style width if available; otherwise, fallback to the offsetWidth
            const colWidth = parseFloat(th.style.width || th.offsetWidth);
            const percent = (colWidth / totalTableWidth) * 100;
    
            // Use a data attribute for fieldName if available, otherwise generate one (e.g., 'col1')
            const fieldName = th.dataset.fieldName || `col${index + 1}`;
    
            currentWidths.push({
                fieldName: fieldName,
                width: percent.toFixed(2) + "%"  // Store the percentage as a string with 2 decimal places
            });
        });
    
        return currentWidths;
    }
    // Return the checked rows
    @api async getCheckedRows() {
        return this.checkboxArray;
    }
    // Remove all the checked Rows
    @api async clearCheckboxes() {
        this.checkboxArray = [];
    }
    // ====================================================================== \\
    
    // Trigger Scrolling if the height of the table exceedes @api maxHeight
    // ---++ Track the table's <relativeContainer> for popover positioning (x-slds-menu-popover-button)
    renderedCallback() {
        console.log('In Rendered Callback');

        // Set the container's max  height
        handleTableScrollBehavior(this, 'custom-table-container');

        // Get the table container to pass to <x-slds-menu-popover-button>
        if (!this.relativeContainer) {
            const tableContainer = this.template.querySelector('.slds-table_edit_container');
            if (tableContainer) {
                this.relativeContainer = tableContainer; 
            }
        }

        if (this.isNewPage) {
            // Check the cells when user returns to the paginated page
            if (this.checkboxArray.length > 0) {
                // Put checked row ids in a set
                const checkedSet = new Set(this.checkboxArray);
                // Get all row checkboxes (data-parent-id has the recordId)
                const checkboxes = this.template.querySelectorAll('[data-element="rowCheckbox"]');
                // Add checkmarks the cells
                checkboxes.forEach(check => {
                    if (checkedSet.has(check.value)) {
                        console.log('got a checked');
                        // Display the checkbox as checked
                        check.checked = true;
                    }
                });
            }

            // Mark the cells as edited when user returns to the paginated page
            if (this.draftValues.length > 0) {  
                // Flatten the draft values so that every field is paired with an id
                const flatDrafts = this.draftValues.flatMap(draft =>
                    Object.entries(draft).filter(([key]) => key !== 'id')
                        .map(([fieldName, value]) => ({ id: draft.id, fieldName })) // add id to every fieldName
                        .map(flat => (`${flat.fieldName}-${flat.id}`))        // recreate data-field-ids of the <td/th class="slds-cell-edits"> elements
                );
                const fieldIdsSet = new Set(flatDrafts); // put the fieldIds in a set

                // Get all editable cells (data-field-id contains 'fieldName-id')
                const fieldCells = this.template.querySelectorAll('.slds-cell-edit');

                // Add 'slds-is-edited' to the edited fields
                fieldCells.forEach(cell => {
                    if (fieldIdsSet.has(cell.dataset.id)) {
                        // add slds-is-edited to the cell
                        cell.classList.add('slds-is-edited');
                    }
                });
            }

            this.isNewPage = false;
        }
        
    }
    // ======================= Parse Rows And Columns ======================= \\
    // Call parseRowsAndColumns()
    connectedCallback() {
        this.parseRowsAndColumns();
    }
    // Format the rows such that the row's fields are in the same order as the columns ---++ Add custom Atrributes to the <columns> & <rows>
    parseRowsAndColumns() {
        try {
            if (this._rows && this.columns) {
                // Map the fields from <rows> so that that each <row.fields> is in the same order as the <column.fieldName>s
                const rowsObj = mapRowsToColumns(this._rows, this.columns);
                this.localRows = rowsObj.rows;    // Get <rows> from response object (keys: 'rows' & 'count')

                // Map the columns to localColumns and add a 'colId' property
                this.localColumns = this.columns.map(col => {
                    return { ...col, colId: `${col.fieldName}-col` };
                });

                // Paginate the rows if <isPaginator> is true
                this.paginateRows(this.localRows);
            }
            
            this.isDataLoaded = true;

        }catch(error) {
            this.showToast('parseRowsAndColumns', error, 'error');
        }
    }
    // ====================================================================== \\

    // ======================== Pagination Handlers ========================= \\
    // Break the rows up into sections of 50 if isPaginator=true
    paginateRows(rowsParam) {
        if  (!this.isPaginator) {
            // Do nothing if not paginated
            return;
        }

        const rowsLength = this.rows.length;             // Number of rows in dataset
        const pageCount = Math.ceil(rowsLength / 50);   // Number of pages --> round up

        if (this.isPaginator) {
            // Add Page count to <pageInfo> object
            this.pageInfo.pageCount = pageCount;

            // Create pages of <this.paginatedRows> (page number as object keys)
            for (let i = 1; i <= pageCount; i++) {
                const startSlice = (i - 1) * 50;
                let endSlice = i * 50;
        
                if (endSlice > rowsLength) {
                    endSlice = rowsLength;
                }
                // Item Reference: '<startSlice> to <endSlice>' && Page Reference: '<i> of <rowsLength>'
                this.pageInfo[`${i}`] = {
                    items: `Items ${startSlice + 1} to ${endSlice}`, 
                    page: `Page ${i} of ${pageCount}`
                };

                // Copy the slice into <paginated Rows> object
                this.paginatedRows[`${i}`] = rowsParam.slice(startSlice, endSlice)
                    .map(row => ({ ...row }));
            }
        }
        // Redefine <localRows>
        this.localRows = this.paginatedRows['1'].map(row => ({...row}));
        // Get the current page's info
        this.pageInfo.activePage = '1';
    }
    // Get the meta for  the paginator combobox options is <isPaginator> is true
    get paginatedRowsMeta() {
        if (this.isPaginator) { 
            
            if (this._sortedCol && this._sortedCol.hasOwnProperty('fieldName') && this._sortedCol.fieldName) {

                // Get the combobox options' icon by referencing the sorted column's data type
                const optionIcon = iconByDataType[this._sortedCol.dtype];

                return Object.keys(this.paginatedRows).map(page => {
                    // Get the fieldName's value of the first row 
                    let metaOne = this.paginatedRows[page][0].fields.find(f => 
                        f.fieldName === this._sortedCol.fieldName
                    )?.label;
                    // Get the fieldName's value of the last row
                    let metaTwo = this.paginatedRows[page][this.paginatedRows[page].length-1].fields.find(f => 
                        f.fieldName === this._sortedCol.fieldName
                    )?.label;

                    // Flag strike through if no metaOne available
                    let isMetaOneStrike = false;
                    // Flag strike through if no metaTwo available
                    let isMetaTwoStrike = false;

                    if (!metaOne) {
                        metaOne = this._sortedCol.label;
                        isMetaOneStrike = true;
                    }
                    if (!metaTwo) {
                        metaTwo = this._sortedCol.label;
                        isMetaTwoStrike = true;
                    }

                    // Add <metaOne> as the second meta if sort type is Descending  (first if ascending)
                    return {
                        label: `Page ${page}`,
                        value: page,
                        icon: optionIcon,
                        metaOne: this._sortedCol.type === 'asc' ? metaOne || '' : metaTwo || '',
                        metaTwo: this._sortedCol.type === 'asc' ? metaTwo || '' : metaOne || '',
                        isMetaOneStrike: isMetaOneStrike,
                        isMetaTwoStrike: isMetaTwoStrike
                    };
                });
            } else {

            }
        }
    }
    // <x-slds-table-paginator>/<x-slds-combobox-search> Event --> Update <localRows> to active page
    handlePageChange(message) {
        const page = message.detail;
        if (page) {
            this.paginatedRows[this.pageInfo.activePage] = this.localRows.map(oldRow => ({...oldRow}));
            this.localRows = this.paginatedRows[page].map(r => ({...r}) );
            this.pageInfo.activePage = page;
            this.isNewPage = true;

        }   
    }
    // ====================================================================== \\



    // ====================== Initialize Edit Popover ======================= \\
    // Display edit popover when cell is clicked
    handleEditClick(event) {
        try  {
            const button = event.currentTarget;
            const cell = button.closest('td, th');

            // Get the field name and record id of the clicked cell
            const currentRecordField = button.dataset.fieldId;
            if (!currentRecordField) return;


            // Initiate the <popoverEdit> Object
            this.fieldIdToPopoverEdit(currentRecordField);

            // Get the popover element
            const popover = this.template.querySelector('.slds-popover_edit');
        
            if (popover && cell) {
                // Get the clicked cell's bounding rect
                const rect = cell.getBoundingClientRect();

                // Convert absolute coordinates to relative inside the container
                const containerRect = this.template.querySelector('.slds-table_edit_container').getBoundingClientRect();
                
                // Get the rect top/left position
                const offsetTop = rect.top - containerRect.top;
                const offsetLeft = rect.left - containerRect.left;
                
                // Convert rect top/left to pixels
                popover.style.top = offsetTop + 'px';
                popover.style.left = offsetLeft + 'px';
                
                // Remove 'hidden' attribute ---++ Add 'show' attribute
                popover.classList.remove('slds-hide');
                popover.classList.add('slds-show');

                // Focus the popover input element
                setTimeout(() => {
                    switch(true) {
                        case this.popoverEdit.dtype === 'richtext': 
                            popover.querySelector('x-slds-rich-text-editor').focusMe();
                            break;

                        case this.popoverEdit.dtype === 'picklist': 
                            popover.querySelector('x-dropdown-plain').focusMe();
                            break;

                        case this.popoverEdit.dtype === 'textarea':
                            popover.querySelector('x-slds-input').focusMe();
                            break;

                        default:
                            popover.querySelector('input').focus();
                            break;
                    }
                }, 30);
            }
        
        }catch(error) {
            this.showToast('handleEditClick', error, 'error');
        }
    }
    // Find the record whose field was edit-clicked
    fieldIdToPopoverEdit(fieldId) {
        try  {
            // Split the dataset.fieldId from the button to get the recordId/fieldName
            const fieldName = fieldId.split('-')[0];
            const recordId = fieldId.split('-')[1];

            // Find the record in the <rows> array
            const record = this.localRows.find(rec => rec.id === recordId);
    
            if (record) {
                // Find the field in the <record.fields> array
                const fieldEntry = record.fields.find(f => f.fieldName === fieldName);

                const boolProp = `is${fieldEntry.dtype}` || 'isText';

                // use the record.field properties to initiate the <popoverEdit> Object
                this.popoverEdit = {
                    id: recordId,
                    fieldName: fieldEntry.fieldName, 
                    label: fieldEntry.label, 
                    value: fieldEntry.value, 
                    dtype: fieldEntry.dtype,
                    fieldId: fieldId,
                    [boolProp]:  true,
                }
                this.popoverEditCopy = {id: recordId, fieldName: fieldEntry.fieldName};
                if (fieldEntry.dtype === 'picklist') {
                    this.picklistOptions = this.localColumns.find(col => col.fieldName === fieldEntry.fieldName).options;
                }
            }

        }catch(error) {
            this.showToast('fieldIdToPopoverEdit', error, 'error');
        }
    }
    // ====================================================================== \\

    
    // ==================== Apply Edit To All Rows Modal ==================== \\
    // Render the modal with the correct messaging
    renderApplyEditsModal() {
        this.modalConfig = {
            tableModalMessage: `Apply edit to the ${this.checkboxArray.length} selected rows?`,
            modalCancelLabel: 'Cancel',
            modalOkayLabel: 'Okay',
            tableModalHandler: this.applyAllEdits.bind(this)
        }
        this.template.querySelector('x-slds-modal').showMe();
    }
    // Accept the response from the child modal component  --->  apply the changes to all the rows if confirmed
    applyAllEdits(message) {
        try {
            const result = message.detail;
            if (result === 'cancel') {
                this.popoverEditCopy = {};
                return;
            } 
            
            this.isLoading =  true;
            const triggerRecFieldId = `${this.popoverEditCopy.fieldName}-${this.popoverEditCopy.id}`;
            const popoverCopy = this.popoverEditCopy;

            const flatPaginatedRows = Object.values(this.paginatedRows).flat();

            for (const recId of this.checkboxArray) {
                // Do nothing to the record that triggered the "Apply all edit"
                if (recId !== popoverCopy.id) {                        
                    // Find the record in the rows
                    let origRec = this.localRows.find(rec => rec.id === recId);
                    if (!origRec) {
                        origRec = flatPaginatedRows.find(rec => rec.id === recId);
                    }
                    // Find the fieldName in the record's fields
                    const origField = origRec.fields.find(field => field.fieldName === popoverCopy.fieldName);
                    // Create a structure replicating the popoverEdit
                    const orignal = {id: recId, fieldName: popoverCopy.fieldName, label: origField.label, value: origField.value};
                    // Create a structure replicating the popoverEdit w/ the updated values
                    const newDraft = {id: recId, fieldName: popoverCopy.fieldName, value: popoverCopy.value, label: popoverCopy.label};
                    const fieldId = `${orignal.fieldName}-${recId}`;

                    this.storeOriginalDraftValue(orignal.id, orignal.fieldName, orignal.value, orignal.label); // Store the current record's original values                
                    this.updateDraftValueOnEdit(newDraft);             // Add the edited field's value to the <draftValues> array
                    this.assignPopOverValueToField(newDraft);         // Update the field's value/label of the record that was edited
                    this.focusCellOnEdit(fieldId, true, false)       // Mark the cells as slds-is-edited
                }

            }
            
            this.focusCellOnEdit(triggerRecFieldId, true);
            this.popoverEditCopy = {};
            this.modalConfig = {};
            this.isLoading = false;
        }catch(error) {
            this.showToast('applyAllEdits', error, 'error');
        }
    }
    // ====================================================================== \\


    // =================== Handle Edit Popover Attributes =================== \\
    // --------------- Local Input Element --------------- \\
    // Get the value sent by the child component (<x-slds-input> / <x-dropdown-plain> / <x-slds-rich-text-editor>)
    handleInputMessage(event) {
        try  {
            // Get the updated value
            const details = event.detail;
            const val = typeof details === 'object'
                ? details[Object.keys(details)[0]]
                : details;


            // Store the original value if the cell was edited
            if (this.popoverEdit && !this.popoverEdit.isEdited && this.popoverEdit.value != val) {
                this.popoverEditCopy.id = this.popoverEditCopy.id;
                // Store the original value of the Record's field
                this.popoverEdit.isEdited = true;
                this.storeOriginalDraftValue(this.popoverEdit.id, this.popoverEdit.fieldName, this.popoverEdit.value, this.popoverEdit.label);
            }   

            if (val !== undefined) {
                // Update the popoverEdit's value 
                this.popoverEdit.value = val;
                this.popoverEdit.label = `${val}` || '';
                this.popoverEditCopy.value = val;
                this.popoverEditCopy.label = `${val}` || '';
            }

            // Assign the value to the drafts ---++  Clear the popoverEdit
            this.finishLocalEdit();
            this.removeEditPopover(true);

        }catch(error) {
            this.showToast('handleEditInputChange', error, 'error');
        }
    }
    // If Escape was not pressed --> save the value in editPopover if it differs from the orginal draft
    inputEleBlur(message=null) {
        try {
            // Abort is prevent blur is flagged
            //if (this.isPreventBlur) {
            //    console.log('input ele blur prevent blur');
            //    this.isPreventBlur = false;
            //    return;
            //}
            if (message) {
                this.removeEditPopover(true);
                return;
            }
            if (!this.isEscape && this.popoverEdit && this.popoverEdit.isEdited) {
                // If was edited --> Store the new values
                this.popoverEditCopy = JSON.parse(JSON.stringify(this.popoverEdit));
                this.finishLocalEdit();
                return;
            }

            // Clear the popover without adding any edits
            this.removeEditPopover(true);
        }catch(error) {
            this.showToast('handlePopoverBlur', error, 'error');
        }

    }
    // Flag 'Escape' keydowns ---++ force blur on 'Enter' click
    inputEleKeyDown(event) {
        try {
            // Check if is a number edit so that user can increment with arrow keys
            if (
                ['isnumber', 'ispercent', 'iscurrency', 'isdecimal'].some(typeBool => this.popoverEdit[typeBool])  
                && ['ArrowUp', 'ArrowDown'].some(arw => event.key === arw) 
            ) {
                this.isPreventBlur = true;
            } 

            if (event.key === 'Escape') {
                this.isEscape = true;
                event.target.blur();

            } else if (event.key === 'Enter') {
                if (this.popoverEdit.isEdited) {
                    this.finishLocalEdit();
                }
                this.isEscape = false;
                event.target.blur();
            } 
        
        }catch(error) {
            this.showToast('editPopoverKeyDown', error, 'error');
        }
    }
    // Prevent blurring if the input element is clicked
    inputEleMouseDown() {
        this.isPreventBlur = true;
    }
    // --------------------------------------------------- \\
    // ====================================================================== \\


    // ======================= Finalize Edit Popover ======================== \\
    // Remove the popover from the DOM
    removeEditPopover(isClearPopoverEdit=false) {
        try {
            const popover = this.template.querySelector('[data-id="popoverEdit"]'); 
            if (popover) {
                popover.classList.add('slds-hide');
                popover.classList.remove('slds-show');
            }
            if (isClearPopoverEdit) {
                this.popoverEdit.isEdited = false;
                this.finishLocalEdit();
                this.popoverEdit = {};
            }
        
        }catch(error) {
            this.showToast('removeEditPopover', error, 'error');
        }
    }

    // Finalize the popoverEdit process --> set the cell to isEdited (if edited) & focus the cell that was clicked
    finishLocalEdit() {
        try {
            if (this.checkboxArray.length > 0) {
                if (this.popoverEdit) {
                    this.renderApplyEditsModal();
                }
            }
            // Get the fieldId to focus the edited cell
            const fieldId = this.popoverEdit.fieldId;
            const isEdited = this.popoverEdit.isEdited || false;

            if (this.popoverEdit.isEdited) {

                if (!this.popoverEdit.fieldId || this.popoverEdit.value === undefined) {
                    this.removeEditPopover(true);
                    return;
                }

                this.updateDraftValueOnEdit();             // Add the edited field's value to the <draftValues> array
                this.assignPopOverValueToField();         // Update the field's value/label of the record that was edited
            }
            // Clear the popoverEdit properties
            this.popoverEdit = {};

            // Focus the cell/editbutton and add class 'slds-is-edited' to the edited cell
            this.focusCellOnEdit(fieldId, isEdited);
                
        
        }catch(error) {
            this.showToast('finishLocalEdit', error, 'error');
        }
    } 
    // Focus the cell that was clicked and set the cell's class to 'slds-is-edited' if the field was edited
    focusCellOnEdit(fieldId, isEdited, isFocus=true) {
        try {
            // Get the cell by the fieldId (i.e. <fieldName-recordId>)
            const editedCell = this.template.querySelector(`[data-id="${fieldId}"]`);
            if (!editedCell) return;

            // For multi-edit situations
            if (!isFocus) {
                // Only mark the cell as slds-is-edited if isFocus=false
                editedCell.classList.add('slds-is-edited');
                return;
            }

            // Get the cell's edit button
            const button = editedCell.querySelector('.slds-button_icon');

            setTimeout(() => {    
                // Set the cell's tabIndex as 0 to make it focusable 
                editedCell.setAttribute("tabIndex", "0");
                
                // If not edited, just apply the focus
                isEdited ? editedCell.classList.add('slds-is-edited', 'slds-has-focus') : editedCell.classList.add('slds-has-focus');

                // Always focus the button after an edit has completed
                button.classList.add('slds-has-focus');
                button.focus();
                button.closest('td').classList.add('slds-has-focus');
            }, 30);

            if (isEdited && !this.isTableEdited) {
                // Display the save/cancel buttons below the table
                this.isTableEdited = true;
            }
        
        }catch(error) {
            this.showToast('focusCellOnEdit', error, 'error');
        }
    }
    // ====================================================================== \\


    // ================== Handle Edit Popover Draft Values ================== \\
    // Store the editing field's Original Value
    storeOriginalDraftValue(recordId, fieldName, value, label) {
        try {
            // Abort if not required information
            if (!recordId || !fieldName) return;

            // Create the new draft entry
            const draftFieldEntry = { value, label };

            // Get the record if it exists already in <draft OriginalValues> 
            const existingDraftEntry = this.draftOriginalValues.find(draft => draft.id === recordId);

            // Add the object directly to the <draft OriginalValues>
            if (!existingDraftEntry) {
                this.draftOriginalValues.push( {id: recordId, [fieldName]: draftFieldEntry} ); // push to array
                return; // Abort
            }

            // Get the existing fieldName entry from <draft OriginalValues> if exists
            const existingFieldEntry = existingDraftEntry[fieldName];

            if (!existingFieldEntry) {
                // Add the field values to the <draftEntry> if not already a key with that fieldName
                existingDraftEntry[fieldName] = draftFieldEntry;
            }

        }catch(error) {
            this.showToast('storeOriginalDraftValue', error, 'error');
        }
    }
    // Store the new Edited Value in Draft Values
    updateDraftValueOnEdit(customDraft=null) {
        try {
            // Enable a custom draft to be sent
            const currentEdit = customDraft ? customDraft : this.popoverEdit;
            
            // Starter entry
            if (this.draftValues.length < 1 || !this.draftValues.some(rec => rec.id === currentEdit.id)) {
                this.draftValues.push({id: currentEdit.id, [currentEdit.fieldName]: currentEdit.value });
                return;
            }
            this.draftValues.forEach(draft => {
                if (draft.id === currentEdit.id) {
                    draft[currentEdit.fieldName] = currentEdit.value;
                }
            });
        }catch(error) {
            this.showToast('updateDraftValueOnEdit', error, 'error');
        }
    }
    // Update the field with the popoverEdit's value
    assignPopOverValueToField(customDraft = null) {
        const currentEdit = customDraft || this.popoverEdit;
    
        try {
            const updateRecord = rec => ({
                ...rec,
                fields: rec.fields.map(field =>
                    field.fieldName === currentEdit.fieldName
                        ? {
                            ...field,
                            value: currentEdit.value,
                            label: this.popoverEdit.isdatetime || this.popoverEdit.isdate
                                ? dateObjToYearFirst(currentEdit.value)
                                : `${currentEdit.value}`
                        }
                        : { ...field }
                )
            });
    
            if (this.localRows.some(rec => rec.id === currentEdit.id)) {
                this.localRows = this.localRows.map(rec =>
                    rec.id === currentEdit.id ? updateRecord(rec) : { ...rec }
                );
            } else {
                const page = Object.keys(this.paginatedRows).find(k =>
                    this.paginatedRows[k].some(rec => rec.id === currentEdit.id)
                );
    
                if (page) {
                    this.paginatedRows = {
                        ...this.paginatedRows,
                        [page]: this.paginatedRows[page].map(rec =>
                            rec.id === currentEdit.id ? updateRecord(rec) : { ...rec }
                        )
                    };
                }
            }
        } catch (error) {
            this.showToast('assignPopOverValueToField', error, 'error');
        }
    }
    // ====================================================================== \\


    // Handle Record-Row Checkbox Click
    handleCheckboxChange(event) {
        try {
          const rowId = event.currentTarget.dataset.parentId;
          const recordId = event.target.value;
          const rowEl = this.template.querySelector(`[data-id="${rowId}"]`);
          
          // toggle the SLDS-selected class and get its new state
          const isSelected = rowEl.classList.toggle('slds-is-selected');
          
          if (isSelected) {
            // add this id
            this.checkboxArray = [...this.checkboxArray, recordId];
          } else {
            // filter it out
            this.checkboxArray = this.checkboxArray.filter(id => id !== recordId);
          }
      
        } catch (error) {
          this.showToast('handleCheckboxChange', error, 'error');
        }
    }

    // ==================== Handle Focus/Blur of Cells ====================== \\
    // Focus a cell on click
    focusCell(event=null, cellParam=null) {
        try {
            const cell = event ? event.currentTarget : cellParam;
            cell.classList.add('slds-has-focus');
            cell.setAttribute("tabindex", "0"); // Make the item focusable
            cell.focus();
        }catch(error) {
            this.showToast('focusCell', error, 'error');
        }
    }
    // Remove slds-has-focus on Blur of Cell
    blurCell(event) {
        try  {
            const cell = event.currentTarget;
            if (event.relatedTarget && cell.contains(event.relatedTarget)) {
                // Focus is still within the cell; do nothing.
                return;
            }
            cell.setAttribute("tabindex", "-1"); // Make the item focusable
            cell.classList.remove('slds-has-focus');
        }catch(error) {
            this.showToast('blurCell', error, 'error');
        }
    }
    // Focus both the cell and the button 
    focusEditButton(event=null, btnParam=null) {
        try {
            const button = event ? event.currentTarget : btnParam;          // Get the button
            const cell = button.closest('td, th');                         // Get the cell
            button.classList.add('slds-has-focus');                       // Add focus to the button 
            cell.setAttribute("tabIndex", "0");                          // Set the cell's tab index to 0
            cell.classList.add('slds-has-focus');                       // Add focus to the cell
        }catch(error) {
            this.showToast('focusEditButton', error, 'error');
        }
    }
    // Blur both the cell and the button 
    blurEditButton(event) {
        try {
            const cell = event.currentTarget.closest('td, th');
            const button = event.currentTarget;
            button.classList.remove('slds-has-focus');
            if (event.relatedTarget && cell.contains(event.relatedTarget)) {
                // Focus is still within the cell; do nothing.
                return;
            }
            cell.classList.remove('slds-has-focus');
            cell.setAttribute("tabIndex", "-1");
        }catch(error) {
            this.showToast('blurEditButton', error, 'error');
        }
    }
    // ======================================================================= \\

    
    // ====================== Handle Column Resizing ========================= \\
    // Find the Column that is being resized and the column widths that follow  ---++  track the total table width
    handlemousedown(event) {
        try {
            event.stopPropagation();
            event.preventDefault();
            // Disable text selection during resizing
            const table = this.template.querySelector('.custom-table-container');
            if (table) {
                table.style.userSelect = 'none';
            }
        
            // Identify the column (th) being resized
            this._tableThColumn = event.target.closest('th');
            if (!this._tableThColumn) return;

        
            // Store initial mouse position and column width
            this._pageX = event.pageX;
            this._tableThWidth = this._tableThColumn.offsetWidth;

            
            // Freeze all column widths
            const tableElement = this.template.querySelector('table');
            const allThs = tableElement.querySelectorAll('thead th');
            const totalTableWidth = tableElement.offsetWidth; // Get table width at the time


            try {
                // Store original widths as percentages if not already set
                if (!this._initWidths) {
                    this._initWidths = [];    
                    allThs.forEach((th) => {
                        const colWidth = th.offsetWidth;
                        const widthPercentage = (colWidth / totalTableWidth) * 100; // Store percentage
                        this._initWidths.push(widthPercentage);
                    });
                }
            } catch (error) {
                this.showToast('Error storing original column widths', error, 'error');
            }


            allThs.forEach((th) => {
                th.style.width = `${th.offsetWidth}px`;
            });
        
            // Add global event listeners for resizing
            document.addEventListener('mousemove', this.handlemousemove.bind(this));
            document.addEventListener('mouseup', this.handlemouseup.bind(this));
        }catch(error) {
            this.showToast('handlemousedown', error, 'error')
        }
        
    }
    // Adjust the column's size as use drags resizer handle ---++ Adjust the table's width as well
    handlemousemove(event) {
        try {
            event.stopPropagation();
            if (!this._tableThColumn) return;
    
            const diffX = event.pageX - this._pageX;
            let newWidth = this._tableThWidth + diffX;
        
            // Enforce a minimum width
            newWidth = Math.max(newWidth, 80);
        
            // Update column width
            this._tableThColumn.style.width = `${newWidth}px`;
        
            // Recalculate total table width
            const tableElement = this.template.querySelector('table');
            const allThs = tableElement.querySelectorAll('thead th');
            const totalTableWidth = Array.from(allThs).reduce(
                (sum, th) => sum + parseFloat(th.style.width || th.offsetWidth),
                0
            );
            tableElement.style.width = `${totalTableWidth}px`;
        
            // Adjust corresponding body cells
            const columnIndex = Array.from(this._tableThColumn.parentNode.children).indexOf(this._tableThColumn);
            const bodyCells = this.template.querySelectorAll(`table tbody tr td:nth-child(${columnIndex + 1})`);
            bodyCells.forEach((cell) => {
                cell.style.width = `${newWidth}px`;
            });
        }catch(error) {
            this.showToast('handlemousemove', error, 'error')
        }
    }
    // Set the table's adjusted width based on the column resize
    handlemouseup(event) {
        try {
            event.stopPropagation();
            // Remove global event listeners
            document.removeEventListener('mousemove', this.handlemousemove.bind(this));
            document.removeEventListener('mouseup', this.handlemouseup.bind(this));
        
            // Reactivate text selection
            const table = this.template.querySelector('.custom-table-container');
            if (table) {
                table.style.userSelect = '';
            }
        
            // Reset references
            this._tableThColumn = null;
            this._pageX = null;
            this._tableThWidth = null;

        }catch(error) {
            this.showToast('handlemouseup', error, 'error')
        }
        
    }
    // ======================================================================= \\
    


    // =========================== Save/Cancel Drafts ========================== \\
    // Remove tabIndex=0 from all the table elements and remove slds-is-edited from all edited cells
    async resetCellClasses() {
        try {
            const allCells = this.template.querySelectorAll('.slds-cell-edit');
            if (allCells) {
                allCells.forEach(cell => {
                    cell.classList.remove('slds-is-edited');
                    cell.setAttribute("tabIndex", "-1");
                });
            }
            const checkboxes = this.template.querySelectorAll('[data-element="rowCheckbox"]');
            if (checkboxes) {
                checkboxes.forEach(checkbox => {
                    checkbox.checked = false;
                });
            }
            this.template.querySelectorAll('[data-element="fieldRow"]').forEach(row => row.classList.remove('slds-is-selected'));
            this.checkboxArray = [];
        }catch(error) {
            this.showToast('resetCellClasses', error, 'error');
        }
       
    }
    // Clear the drafts
    async clearDrafts() {
        try {
            this.draftOriginalValues = [];
            this.draftValues = [];
            if (this.popoverEdit) {
                this.popoverEdit = {};
            }

        }catch(error) {
            this.showToast('clearDrafts', error, 'error')
        }
    }
    // Map the original values back to all the rows ---++ Reset the <td> and <th> cell styling classes
    async cancelEdits() {
        this.isLoading = true;
        try {
            // Get all the edited rows' ids in a set
            const rowIdsSet = new Set(this.draftOriginalValues.map(draft => draft.id));
            
            // Map the rows back to their original values/labels
            this.localRows = this.localRows.map(row => {

                if (!rowIdsSet.has(row.id)) {
                    // Do nothing to the row if not in the set of edited Ids
                    return {...row};
                }

                // Get the the draft object  --->  structure: { id: <id> <field1>: {label: <label> value: <value> } } 
                const draftObj = this.draftOriginalValues.find(draft => draft.id === row.id);

                // Map the original Fields' value/label back to the row
                return {
                    ...row,
                    fields: row.fields.map(field => {
                        // Get the original field's label and value
                        const draftField = draftObj[field.fieldName];

                        return draftField 
                            ? {...field, label: draftField.label, value: draftField.value}
                            : {...field};
                    })
                };
            });

            // Reset the datatable attributes cell styles
            await this.clearDrafts();
            await this.resetCellClasses();
            this.isTableEdited = false;

        }catch(error) {
            this.showToast('cancelEdits', error, 'error');
            this.isLoading = false;
        }
        this.isLoading = false;
    }
    // Map the drafts to a record-format --> Save the record-formats to the database ---++ Reset the <td> and <th> cell styling classes
    async saveEdits() {
        this.isLoading = true;
        try {
            // Save the edited fields to MySQL
            await this.saveEditedTrades(this.draftValues);

            // Reset the datatable attributes cell styles
            await this.clearDrafts();
            await this.resetCellClasses();

            this.isTableEdited = false;

        } catch(error) {
            this.showToast('saveEdits', error, 'error');
            this.isLoading = false;
        }

        this.isLoading = false;
    }
    // ========================================================================= \\


    // ======================== Paginator Footer Handler ======================= \\
    // Handle the message in the parent
    handleToggle(message) {
        if (message.detail === 'on') {
            console.log('on');
        } else if (message.detail === 'off') {
            console.log('off');
        }
    }
    // Handle paginator clicks
    changePage(event) {
        console.log('changing page');
    }
    handleDumb(message) {
        console.log(messsage);
    }
    // ========================================================================= \\



    
    // ===================== Toast ===================== \\
    toastObj;
    showToast(title, message, variant, mode=null) {
        const modeP = mode 
            ? mode 
                : variant === 'success'
            ? 'dismissible'
            : 'sticky';
        
        this.toastObj = { 
            variant: variant, 
            mode: modeP, 
            message: message,
            header: { 
                text: title
            }        
        };
    }
    // ================================================= \\


    // Call Flask to save the data
    async saveEditedTrades(editedRows) {
        try {
            const resp = await fetch('/api/trades', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editedRows)
            });
      
          const payload = await resp.json();

          if (!resp.ok || !payload.success) {
            throw new Error(payload.error || `HTTP ${resp.status}`);
          }

          console.log('Trades updated successfully');
          // optionally re‑load your table or display a toast

        } catch (err) {
            this.showToast('saveEditedTrades', err, 'error');
        }
    }



    // =================================================================== Column Sorting Handlers =================================================================== \\
    // Handle Sort-Button of Column
    sortCol(event) {
        this.isLoading = true;
        const button = event.currentTarget;
        const fieldName = button.dataset.colFieldName;
        let isAsc = false;

        setTimeout(async () => {
            try {
                const th = button.closest('th');

                // Remove slds-is-sort and set aria-sort to none
                function removeSort(tableCols) {
                    if (tableCols) {
                        tableCols.forEach(col => {
                            if (col.classList.contains('slds-is-sorted') && col.dataset.id !== th.dataset.id) {
                                col.classList.remove('slds-is-sorted', 'slds-is-sorted_asc', 'slds-is-sorted_desc');
                                col.setAttribute('aria-sort', 'none');
                            }
                        });
                    } 
                }
                // Add slds-is-sorted_desc and set aria-sort to descending
                function sortColDesc(col) {
                    if (!col.classList.contains('slds-is-sorted')) { col.classList.add('slds-is-sorted'); }
                    col.classList.remove('slds-is-sorted_asc');
                    col.setAttribute('aria-sort', 'descending');
                    col.classList.add('slds-is-sorted_desc');
                }

                // Add slds-is-sorted_asc and set aria-sort to ascending
                function sortColAsc(col) {
                    if (!col.classList.contains('slds-is-sorted')) { col.classList.add('slds-is-sorted'); }
                    col.setAttribute('aria-sort', 'ascending');

                    col.classList.remove('slds-is-sorted_asc');
                    col.classList.add('slds-is-sorted_asc');
                }

                if (th) { 
                    // Remove Sorting Atribute from all other cols
                    const tableCols = this.template.querySelectorAll('[data-element="tableColumn"]');
                    removeSort(tableCols);

                    if  (!th.classList.contains('slds-is-sorted')) {
                        sortColAsc(th);
                        isAsc = true;
                    } else  {
                        if (th.getAttribute('aria-sort') === 'descending') {   
                            sortColAsc(th);  
                            isAsc = true;
                        } else {
                            sortColDesc(th);
                            isAsc = false;
                        }
                    
                    }
                } else {
                    console.warn('Could not find <th> from button');
                }

                // Perform the sort
                await this.performSort(isAsc, fieldName);
                this.isLoading = false;

            }catch(error) {
                this.isLoading = false;
                this.showToast('sortCol', error, 'error')
            }
            finally {
                const field = this.localColumns.find(col => col.fieldName === fieldName);
                const fieldLabel = field.Label;
                this._sortedCol = {fieldName: field.fieldName, label: field.label, type: isAsc ? 'asc' : 'desc', dtype: field.dtype || 'text'};
                const ascType = isAsc ? 'Ascending' : 'Descending';
                this.dispatchEvent(new CustomEvent('newsort', { detail: this._sortedCol } ));
                this.isLoading = false;
              }
        }, 0);
    }
    // Sort the columns after changes detected
    async performSort(isAsc, fieldName) {
        try {
            let sortingRows = this.isPaginator 
                ? [...Object.values(this.paginatedRows).flat()]
                : [...this.localRows];

            let picklistOptions = [];
            const dtype = this.localColumns.find(col => col.fieldName === fieldName).dtype;

            if (dtype === 'picklist') {
                picklistOptions = this.localColumns.find(col => col.fieldName === fieldName).options
                    .map(option => option.value);
            }

            // Perform Sorting
            sortingRows = sortingRows.sort((a, b) => {
                const fieldA = a.fields.find(field => field.fieldName === fieldName);
                const fieldB = b.fields.find(field => field.fieldName === fieldName);
                
                const valueA = fieldA?.value || '';
                const valueB = fieldB?.value || '';
        
                const dtype = fieldA?.dtype || 'text';
        
                // Blank text handling
                if (!valueA && !valueB) return 0;
                if (!valueA) return isAsc ? -1 : 1;
                if (!valueB) return isAsc ? 1 : -1;
        
                if (dtype === 'picklist' && picklistOptions) {
                    // Sort picklist by options order
                    const indexA = picklistOptions.indexOf(valueA);
                    const indexB = picklistOptions.indexOf(valueB);
        
                    return isAsc ? indexA - indexB : indexB - indexA;
                } else if (dtype === 'number' || dtype === 'decimal' || dtype === 'currency' || dtype === 'percent') {
                    // Numeric sorting
                    return isAsc ? valueA - valueB : valueB - valueA;
                } else if (dtype === 'date') {
                    // Date sorting
                    return isAsc
                        ? new Date(valueA) - new Date(valueB)
                        : new Date(valueB) - new Date(valueA);
                
                 // datetime
                } else if (dtype === 'datetime') {
                    const tA = Date.parse(valueA);
                    const tB = Date.parse(valueB);
                    if (isNaN(tA) && isNaN(tB)) return 0;
                    if (isNaN(tA)) return isAsc ? -1 : 1;
                    if (isNaN(tB)) return isAsc ? 1 : -1;
                    return isAsc
                    ? tA - tB
                    : tB - tA;
                
                } else {
                    // Default alphabetical sorting with tie-breaker
                    const splitA = valueA.split(' ');
                    const splitB = valueB.split(' ');
        
                    for (let i = 0; i < Math.max(splitA.length, splitB.length); i++) {
                        const wordA = splitA[i] || '';
                        const wordB = splitB[i] || '';
                        const comparison = wordA.localeCompare(wordB);
                        if (comparison !== 0) {
                            return isAsc ? comparison : -comparison;
                        }
                    }
                    return 0;
                }
            });

            if (this.isPaginator) {
                this.paginateRows(sortingRows);
                return;
            } 

            this.localRows = JSON.parse(JSON.stringify(sortingRows));

        }catch(error) {
            this.showToast('sldsTable --> performSort()', error, 'error');
        }
    }
    // ================================================================================================================================================================ \\


    // Apply Styles to the host 👉 Can pass 1.Array of Arrays   2.Array of Object   3.Strings
    _applyVarsDirect(cssVar, varStyle=null) {

        // Check that CSS var starts with '--' and has a non-null stlye 
        const isValid = (cssVarP, varStyleP) => {
            return typeof cssVarP === 'string' && 
                typeof varStyleP === 'string' &&
                cssVarP?.startsWith('--') && 
                varStyleP;
        }



        const el = this.template.host;
        
        if (Array.isArray(cssVar)) {
            // Handle Array of vars
            cssVar.forEach(hostVar => {
                if (Array.isArray(hostVar) && isValid(hostVar[0], hostVar[1])) {
                    // Handle Array structure  (CSS var at index 0)
                    el.style.setProperty(hostVar[0], hostVar[1]);
                } else if (typeof hostVar === 'object' && isValid(hostVar.cssVar, hostVar.varStyle)) {
                    // Handle Object structure (cssVar & varStyle keys)
                    el.style.setProperty(hostVar.cssVar, hostVar.varStyle)
                }
            });
        } else if (isValid(cssVar, varStyle)) {
            // Handle string arguments
            el.style.setProperty(cssVar,  varStyle);
        }
    }

}